/**
 * `workflowTextTokens.ts` — the RUNTIME substitutions a workflow's authored
 * prose may carry. Web-first: the desktop client has no workflow text tokens
 * at all.
 *
 * One token today, `<user name>`, which resolves to the signed-in learner's
 * first name wherever it appears inside a learner-facing string. It is a
 * SUBSTITUTION, not a whole-value token, which is what separates it from
 * `SAY_STEP_INSTRUCTIONS_TOKEN` (`WorkflowDocument.ts`) — read both before
 * adding a third, because the two answer different questions:
 *
 *   - `<step_instructions>` is a whole-value token, legal only as a `say:`
 *     value, resolved against the DOCUMENT (this step's own copy).
 *   - `<user name>` is an inline substitution, legal anywhere in learner-facing
 *     prose, resolved against RUNTIME state (who is signed in right now).
 *
 * A token rather than a new key, for the reason the spec already records for
 * its sibling: it changes nothing about the file's shape. Every existing
 * document still loads, the codec, the patch schema, the authored view and both
 * editor lexers are untouched, and a workflow that never uses it is byte
 * identical to one written before this existed.
 *
 * ## Where it resolves, and where it must NOT
 *
 * At the DISPLAY / SPEAK boundary of playback, never at parse and never in the
 * document model:
 *
 *   - `WorkflowRunModel.getPlaybackView()` — instructions, button labels,
 *     trigger labels, media name / description / transcript.
 *   - `WorkflowScriptRunner` — a `say:` line, after `resolveSayText`, so a
 *     scripted read-out speaks the name rather than the brackets.
 *
 * Resolving at parse would bake ONE learner's name into a document every other
 * learner then reads, which is the whole reason the indirection exists. And the
 * authored view, the codec and `get_workflow` all deliberately keep the literal
 * token: the author has to be able to see and edit it, and the agent has to be
 * able to WRITE it without a resolved name being saved back into a shared file.
 *
 * ## NAMES are excluded, deliberately and permanently
 *
 * A step's name, a group's name and a workflow's name are IDENTIFIERS, not
 * prose. `go_to`, a trigger target, the visit stack, the persisted progress row
 * and the agent's `go_to_workflow_step step=<name>` all address a step by its
 * name, and every one of those comparisons is document-name against
 * document-name. Substituting into a displayed name would show the learner
 * "Welcome Alex" for a step the document calls "Welcome <user name>" — so
 * asking to go back to the step they can see would name something that does not
 * exist. The gap is real and stated in the spec: a name shows its token raw.
 */

import type { UserInfo } from "@models/auth/types";

/**
 * The canonical spelling, and the one every agent-facing surface teaches.
 *
 * The `<user_name>` alias below is accepted too — snake_case is what every
 * identifier in this format uses, so it is the one near-miss an author is
 * genuinely likely to type, and a missed substitution does not fail quietly:
 * it puts raw angle brackets in front of a learner mid-lesson. This is NOT the
 * "two spellings forever" hazard the key rules warn about, because nothing here
 * is serialized — the author's own text round-trips verbatim either way, so the
 * alias costs one alternation in one regex and buys back the commonest typo.
 *
 * `<username>` (one word) is deliberately NOT accepted: it means a login handle
 * elsewhere in this codebase (`cognito:username` is the user id), and quietly
 * substituting a first name for it would be answering a different question.
 */
export const USER_NAME_TOKEN = "<user name>";

/**
 * Both spellings, case-insensitively — an author writing `<User name>` at the
 * start of a sentence means the same thing.
 *
 * TWO literals rather than one shared object, and the second is the ONLY /g/
 * one. A global regex carries `lastIndex` across calls, so a `test` that
 * matches leaves it mid-string and the NEXT call on a different string starts
 * from there and answers wrong — a bug that needs a reset before every use and
 * silently returns whenever someone forgets. The non-global tester cannot hold
 * that state at all, and the global one is only ever handed to `replace`, which
 * is specified to start from 0 and reset when it finishes.
 */
const K_USER_NAME_TEST = /<user[ _]name>/i;
const K_USER_NAME_REPLACE = /<user[ _]name>/gi;

/** Runtime facts the authored prose may refer to. */
export interface WorkflowTextTokenContext {
  /**
   * What `<user name>` resolves to. Empty when nothing is known — see
   * {@link resolveUserFirstName} for why that is close to unreachable for a
   * signed-in user.
   */
  readonly userFirstName: string;
}

/**
 * The learner's first name, with the fallback chain this repo already answers
 * the same question with once (`buildNameTag`, `AmplifyAuthModel.ts`): the
 * given name from the token, else the first word of the display name — which
 * itself falls back to the email's local part, so a signed-in user essentially
 * always has SOMETHING.
 *
 * Empty only with no signed-in user at all, which during a run cannot happen
 * (the route guard requires auth) and is reachable only headlessly. The token
 * then resolves to nothing rather than to an invented word: a generic stand-in
 * reads fine in the vocative case the author probably wrote ("Welcome,
 * <user name>") and as nonsense in every other one ("hand the bracket to
 * <user name>"), so there is no single word that is safe across the uses this
 * token is open to.
 */
export function resolveUserFirstName(user: UserInfo | null | undefined): string {
  if (!user) return "";
  const given = user.firstName?.trim() ?? "";
  if (given.length > 0) return given;
  // The display name's own fallback is the email local part, so this is the
  // last rung that can still produce something recognisable.
  return user.displayName.trim().split(/\s+/)[0] ?? "";
}

/**
 * Whether a string carries any runtime token.
 *
 * Deliberately NOT exported: its only caller is the early return below. An
 * exported predicate nothing outside this file asks is the dead-option trap
 * this repo keeps re-learning — export it in one line the day something needs
 * to gate on the question rather than on the answer.
 */
function hasWorkflowTextToken(text: string): boolean {
  return K_USER_NAME_TEST.test(text);
}

/**
 * Substitutes every runtime token in one authored string.
 *
 * Returns the input UNCHANGED when it carries no token, so the overwhelmingly
 * common case allocates nothing — this runs over every field of every playback
 * view read, and the panel re-reads that view on each run-state emit.
 */
export function resolveWorkflowText(text: string, ctx: WorkflowTextTokenContext): string {
  if (text.length === 0 || !hasWorkflowTextToken(text)) return text;
  // A FUNCTION replacement, deliberately: a name is data, and a `$&` or `$1`
  // inside one would otherwise be read as a backreference and expand.
  return text.replace(K_USER_NAME_REPLACE, () => ctx.userFirstName);
}
